import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const STATUSES = ["pending", "approved", "discarded", "all"];

export default function BioReview() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bio-review?status=${status}`);
      setItems(data);
      setDrafts(Object.fromEntries(data.map((p) => [p.id, {
        bio: p.bio_draft || p.bio || "",
        research_note: p.research_note_draft || p.research_note || "",
      }])));
    } catch (err) {
      setItems([]);
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/bio-review/${id}/${action}`, action === "approve" ? drafts[id] : {});
      toast.success(action === "approve" ? "Published on the profile" : "Discarded — nothing published");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  const edit = (id, key, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  return (
    <div data-testid="admin-bio-review">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl uppercase tracking-tight text-white">AI biographies</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">
            Machine-written prose about a named, living person never publishes itself. Read it, correct anything
            you cannot stand behind, and approve only what you would defend. Discard and the profile keeps the
            sourced facts with no prose at all.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="bio-review-refresh"
          className="mono flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-300 hover:border-ochre hover:text-ochre"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            data-testid={`bio-review-status-${s}`}
            className={`mono border px-4 py-2 text-[0.55rem] uppercase tracking-[0.16em] ${
              status === s ? "border-ochre bg-ochre text-black" : "border-white/15 text-zinc-400 hover:border-ochre hover:text-ochre"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-12 flex items-center gap-3 text-sm text-zinc-500" data-testid="bio-review-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !items.length ? (
        <p className="mt-12 text-sm text-zinc-500" data-testid="bio-review-empty">
          Nothing {status === "all" ? "here" : status} — no machine-written prose is waiting on you.
        </p>
      ) : (
        <div className="mt-10 space-y-8">
          {items.map((p) => (
            <div key={p.id} className="border border-white/10 bg-[#0b0b0b] p-6" data-testid={`bio-review-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-lg uppercase tracking-tight text-white" data-testid={`bio-review-name-${p.id}`}>
                    {p.name}
                  </h2>
                  <p className="mono mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                    {[p.club, p.league].filter(Boolean).join(" · ") || "Club unrecorded"} · {p.bio_source || "AI research job"}
                  </p>
                </div>
                <span className="mono border border-white/15 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400"
                  data-testid={`bio-review-status-tag-${p.id}`}>
                  {p.bio_status || "none"}
                </span>
              </div>

              {p.bio_status === "pending" ? (
                <>
                  <label className="mono mt-6 block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                    Profile paragraph
                  </label>
                  <textarea
                    value={drafts[p.id]?.bio || ""}
                    onChange={(e) => edit(p.id, "bio", e.target.value)}
                    rows={5}
                    data-testid={`bio-review-text-${p.id}`}
                    className="mt-2 w-full border border-white/15 bg-black p-4 text-sm leading-relaxed text-zinc-200 focus:border-ochre focus:outline-none"
                  />
                  <label className="mono mt-5 block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                    Heritage / research note
                  </label>
                  <textarea
                    value={drafts[p.id]?.research_note || ""}
                    onChange={(e) => edit(p.id, "research_note", e.target.value)}
                    rows={3}
                    data-testid={`bio-review-note-${p.id}`}
                    className="mt-2 w-full border border-white/15 bg-black p-4 text-sm leading-relaxed text-zinc-200 focus:border-ochre focus:outline-none"
                  />

                  {p.sources?.length ? (
                    <ul className="mt-4 space-y-1" data-testid={`bio-review-sources-${p.id}`}>
                      {p.sources.map((s) => (
                        <li key={s} className="truncate text-xs text-zinc-500">{s}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-xs text-maasai" data-testid={`bio-review-nosources-${p.id}`}>
                      No sources attached — do not publish prose you cannot evidence.
                    </p>
                  )}

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={busy === p.id}
                      onClick={() => act(p.id, "approve")}
                      data-testid={`bio-review-approve-${p.id}`}
                      className="mono flex items-center gap-2 bg-ochre px-5 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-black disabled:opacity-40"
                    >
                      <Check className="h-3.5 w-3.5" /> Approve and publish
                    </button>
                    <button
                      type="button"
                      disabled={busy === p.id}
                      onClick={() => act(p.id, "discard")}
                      data-testid={`bio-review-discard-${p.id}`}
                      className="mono flex items-center gap-2 border border-maasai/60 px-5 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-maasai disabled:opacity-40"
                    >
                      <X className="h-3.5 w-3.5" /> Discard
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-5 text-sm leading-relaxed text-zinc-300" data-testid={`bio-review-published-${p.id}`}>
                    {p.bio || "No prose on this profile."}
                  </p>
                  {p.bio_approved_by ? (
                    <p className="mono mt-4 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                      Approved by {p.bio_approved_by} · {p.bio_approved_at}
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
